import { z } from "zod";
import { clientRecordSchema, itemPayloadSchema, itemTypeSchema, ncjmmStepSchema } from "./items";
import { itemResponseSchema } from "./scoring";
import { setSummarySchema } from "./sets";

/** Study contracts (spec §7.2 Questions / Sessions, Verify / Flags, Mind Maps, search). */

export const questionSchema = z.object({
  id: z.string().uuid(),
  set_id: z.string().uuid(),
  item_type: itemTypeSchema,
  payload: itemPayloadSchema,
  source_chunk_ids: z.array(z.string().uuid()),
  blueprint_tags: z.record(z.string(), z.string()),
  difficulty: z.number(),
  case_id: z.string().uuid().nullable(),
  /** The clinical judgment step a case-study item measures (plan BE12). */
  ncjmm_step: ncjmmStepSchema.nullable().default(null),
  status: z.enum(["active", "flagged"]),
});
export type Question = z.infer<typeof questionSchema>;

/** GET /case-studies/:id — the shared client record a case's six items are about. */
export const caseStudySchema = z.object({
  id: z.string().uuid(),
  set_id: z.string().uuid(),
  title: z.string(),
  client_record: clientRecordSchema,
  source_chunk_ids: z.array(z.string().uuid()),
});
export type CaseStudy = z.infer<typeof caseStudySchema>;

export const SESSION_MODES = ["quiz_tutor", "quiz_exam", "flashcards"] as const;
export const sessionModeSchema = z.enum(SESSION_MODES);
export type SessionMode = z.infer<typeof sessionModeSchema>;

export const createSessionRequestSchema = z.object({
  set_id: z.string().uuid(),
  mode: z.enum(["quiz_tutor", "quiz_exam"]),
  count: z.number().int().min(1).max(100),
  /** Blueprint category keys (first dimension) to draw from; empty = all. */
  topics: z.array(z.string()).max(30).default([]),
  /** Exam mode only: total time; null = untimed. */
  timed_seconds: z.number().int().min(60).max(6 * 3600).nullable().default(null),
});
export type CreateSessionRequest = z.infer<typeof createSessionRequestSchema>;

export const answerRecordSchema = z.object({ question_id: z.string().uuid(), score: z.number(), correct: z.boolean() });

export const sessionSchema = z.object({
  id: z.string().uuid(),
  /** null for "Today's session", which draws from all of the student's sets (plan BE7). */
  set_id: z.string().uuid().nullable(),
  mode: sessionModeSchema,
  timed_seconds: z.number().int().nullable(),
  started_at: z.string(),
  completed_at: z.string().nullable(),
  items: z.array(questionSchema),
  answers: z.array(answerRecordSchema),
});
export type Session = z.infer<typeof sessionSchema>;

export const answerRequestSchema = z.object({
  question_id: z.string().uuid(),
  response: itemResponseSchema,
  seconds: z.number().int().min(0).max(6 * 3600),
});
export type AnswerRequest = z.infer<typeof answerRequestSchema>;
export const answerResponseSchema = answerRecordSchema;

export const sessionSummarySchema = z.object({
  id: z.string().uuid(),
  /** Null for a cross-set "review due today" run. */
  set_id: z.string().uuid().nullable(),
  mode: sessionModeSchema,
  total: z.number().int(),
  answered: z.number().int(),
  correct: z.number().int(),
  score_percent: z.number().int().min(0).max(100),
  duration_seconds: z.number().int(),
  due_cards: z.number().int(),
});
export type SessionSummary = z.infer<typeof sessionSummarySchema>;

/** Flashcard sessions are logged too (streaks, weekly report): POST /sessions/flashcards. */
export const flashcardSessionRequestSchema = z.object({
  set_id: z.string().uuid().nullable(),
  reviewed: z.number().int().min(1).max(1000),
  duration_seconds: z.number().int().min(0).max(6 * 3600),
  client_session_id: z.string().min(8).max(80),
});

// ---------------------------------------------------------------- verify & flags (§5.2 flags, screen #15)
export const FLAG_REASONS = ["wrong_answer", "not_in_source", "unclear", "other"] as const;
export const FLAG_REASON_LABELS: Record<(typeof FLAG_REASONS)[number], string> = {
  wrong_answer: "The answer is wrong",
  not_in_source: "It's not in my material",
  unclear: "It's confusing",
  other: "Something else",
};
export const createFlagRequestSchema = z.object({
  item_kind: z.enum(["question", "card"]),
  item_id: z.string().uuid(),
  reason: z.enum(FLAG_REASONS),
  note: z.string().trim().max(500).optional(),
});
export type CreateFlagRequest = z.infer<typeof createFlagRequestSchema>;
export const flagSchema = z.object({
  id: z.string().uuid(),
  item_kind: z.enum(["question", "card", "tutor_message"]),
  item_id: z.string().uuid(),
  reason: z.string(),
  status: z.enum(["open", "resolved", "dismissed"]),
  created_at: z.string(),
});
export const flagListSchema = z.object({ items: z.array(flagSchema) });

/** Citation text shown beside an answer (Verify, rationale "Source: Slide 23"). */
export const chunkSchema = z.object({ id: z.string().uuid(), anchor: z.string(), text: z.string(), source_filename: z.string() });
export const chunkListSchema = z.object({ items: z.array(chunkSchema) });
export type SourceChunk = z.infer<typeof chunkSchema>;

// ---------------------------------------------------------------- cards: edit / create (Pro, screen #34)
export const cardWriteSchema = z.object({
  front: z.string().trim().min(1, "Add the front of the card").max(400),
  back: z.string().trim().min(1, "Add the back of the card").max(800),
  /** Must stay cited (§6.4); an empty list marks the card as written by the student. */
  source_chunk_ids: z.array(z.string().uuid()).max(5),
});
export type CardWrite = z.infer<typeof cardWriteSchema>;

// ---------------------------------------------------------------- guide & mind map (screens #32, #18)
export const guideSchema = z.object({
  sections: z.array(z.object({ heading: z.string(), body: z.string(), source_chunk_ids: z.array(z.string().uuid()) })),
});

export interface MindMapTree {
  label: string;
  source_chunk_ids: string[];
  children: MindMapTree[];
}
export const mindMapNodeSchema: z.ZodType<MindMapTree> = z.lazy(() =>
  z.object({ label: z.string().trim().min(1).max(80), source_chunk_ids: z.array(z.string().uuid()), children: z.array(mindMapNodeSchema).max(12) }),
);
export const mindMapSchema = z.object({ tree: mindMapNodeSchema, edited: z.boolean() });

// ---------------------------------------------------------------- library & search (screen #33)
export const searchResponseSchema = z.object({
  sets: z.array(setSummarySchema),
  cards: z.array(z.object({ id: z.string().uuid(), set_id: z.string().uuid(), set_title: z.string(), front: z.string(), back: z.string() })),
  questions: z.array(z.object({ id: z.string().uuid(), set_id: z.string().uuid(), set_title: z.string(), stem: z.string() })),
});
export type SearchResponse = z.infer<typeof searchResponseSchema>;

/**
 * Student-facing text from generated content: drops the source markers the writer leaves in ("[c1]", "[c2, c3]") and a
 * leading option id ("r1: Because…"), so nothing internal reaches the screen.
 */
export function displayText(text: string): string {
  return text
    .replace(/\s*\[c\d+(?:\s*,\s*c\d+)*\]/gi, "")
    .replace(/^\s*[a-z]{1,2}\d{1,2}\s*[:：]\s+/i, "")
    .trim();
}

/** True when the question's own wording already tells the student to select every correct answer. */
export const stemSaysSelectAll = (stem: string): boolean => /select all|choose all|all that apply/i.test(stem);
